import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChevronRight, Folder, FolderOpen, Plus, MoreHorizontal, Pencil, Palette, Trash2, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useFolders, type FolderTreeNode } from "@/hooks/useFolders";
import { CreateFolderDialog } from "@/components/folders/CreateFolderDialog";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { useDocumentDrag } from "@/contexts/DocumentDragContext";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";

const FOLDER_COLORS = [
  { value: "#6366f1", label: "Indigo" },
  { value: "#8b5cf6", label: "Violet" },
  { value: "#ec4899", label: "Rose" },
  { value: "#ef4444", label: "Rouge" },
  { value: "#f97316", label: "Orange" },
  { value: "#eab308", label: "Jaune" },
  { value: "#22c55e", label: "Vert" },
  { value: "#14b8a6", label: "Teal" },
  { value: "#0ea5e9", label: "Bleu" },
  { value: "#64748b", label: "Gris" },
];

interface FolderTreeProps {
  collapsed?: boolean;
}

export function FolderTree({ collapsed = false }: FolderTreeProps) {
  const { folderTree, loading, renameFolder, deleteFolder, updateFolderColor, refetch } = useFolders();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  if (loading) {
    return (
      <div className="space-y-2 px-3">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-3/4" />
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {/* Header with create button */}
      <div className="flex items-center justify-between px-3 mb-2">
        {!collapsed && (
          <p className="text-[11px] text-muted-foreground/70 uppercase tracking-widest font-semibold">
            Mes dossiers
          </p>
        )}
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-muted-foreground hover:text-foreground"
              onClick={() => setCreateDialogOpen(true)}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">
            Nouveau dossier
          </TooltipContent>
        </Tooltip>
      </div>

      {/* Folder tree */}
      {folderTree.length === 0 ? (
        !collapsed && (
          <p className="text-xs text-muted-foreground/60 px-3 py-2 italic">
            Aucun dossier
          </p>
        )
      ) : (
        <div className="space-y-0.5">
          {folderTree.map((folder) => (
            <FolderItem
              key={folder.id}
              folder={folder}
              level={0}
              collapsed={collapsed}
              onRename={renameFolder}
              onDelete={deleteFolder}
              onColorChange={updateFolderColor}
              onRefetch={refetch}
            />
          ))}
        </div>
      )}

      <CreateFolderDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />
    </div>
  );
}

interface FolderItemProps {
  folder: FolderTreeNode;
  level: number;
  collapsed: boolean;
  onRename: (folderId: string, newName: string) => Promise<void>;
  onDelete: (folderId: string) => Promise<void>;
  onColorChange: (folderId: string, color: string) => Promise<void>;
  onRefetch: () => Promise<void>;
}

function FolderItem({ folder, level, collapsed, onRename, onDelete, onColorChange, onRefetch }: FolderItemProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  
  // Dialog states
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [colorDialogOpen, setColorDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [newName, setNewName] = useState(folder.name);
  const [newColor, setNewColor] = useState(folder.color);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [folderStats, setFolderStats] = useState<{ documents: number; subfolders: number } | null>(null);
  
  const { isDragging, dropOnFolder } = useDocumentDrag();

  const activeFolderId = searchParams.get("folder");
  const isActive = activeFolderId === folder.id;
  const hasChildren = folder.children.length > 0;

  const handleClick = () => {
    navigate(`/documents?folder=${folder.id}`);
  };

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  // Drag & Drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isDragging) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    if (isDragging) {
      await dropOnFolder(folder.id, folder.name);
    }
  };

  // Actions
  const handleRename = async () => {
    if (!newName.trim() || newName === folder.name) {
      setRenameDialogOpen(false);
      return;
    }
    setIsSubmitting(true);
    try {
      await onRename(folder.id, newName.trim());
      await onRefetch();
      setRenameDialogOpen(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleColorChange = async (color: string) => {
    setNewColor(color);
    setIsSubmitting(true);
    try {
      await onColorChange(folder.id, color);
      await onRefetch();
      setColorDialogOpen(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = async () => {
    // Fetch stats before showing dialog
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const [docsResult, subfoldersResult] = await Promise.all([
      supabase
        .from("documents")
        .select("id", { count: "exact", head: true })
        .eq("folder_id", folder.id)
        .is("deleted_at", null),
      supabase
        .from("folders")
        .select("id", { count: "exact", head: true })
        .eq("parent_id", folder.id)
    ]);

    setFolderStats({
      documents: docsResult.count || 0,
      subfolders: subfoldersResult.count || 0
    });
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    setIsSubmitting(true);
    try {
      // First, move all documents to root (folder_id = null)
      if (folderStats && folderStats.documents > 0) {
        await supabase
          .from("documents")
          .update({ folder_id: null })
          .eq("folder_id", folder.id);
      }
      
      // Move subfolders to root
      if (folderStats && folderStats.subfolders > 0) {
        await supabase
          .from("folders")
          .update({ parent_id: null })
          .eq("parent_id", folder.id);
      }

      await onDelete(folder.id);
      await onRefetch();
      setDeleteDialogOpen(false);
      
      // If we're viewing this folder, navigate to root
      if (activeFolderId === folder.id) {
        navigate("/documents");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const FolderIcon = isExpanded && hasChildren ? FolderOpen : Folder;

  const folderButton = (
    <button
      onClick={handleClick}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={cn(
        "group relative flex items-center gap-2 w-full rounded-lg transition-all duration-200 text-left",
        collapsed ? "px-3 py-2 justify-center" : "px-3 py-2",
        isActive
          ? "bg-primary/10 text-primary"
          : "text-muted-foreground hover:bg-muted/50 hover:text-foreground",
        isDragOver && isDragging && "bg-primary/20 ring-2 ring-primary ring-offset-1"
      )}
      style={{ paddingLeft: collapsed ? undefined : `${12 + level * 12}px` }}
    >
      {/* Expand/collapse arrow for folders with children */}
      {hasChildren && !collapsed && (
        <button
          onClick={handleToggle}
          className="p-0.5 hover:bg-muted rounded transition-colors"
        >
          <ChevronRight
            className={cn(
              "h-3 w-3 transition-transform duration-200",
              isExpanded && "rotate-90"
            )}
          />
        </button>
      )}

      {/* Folder icon with color */}
      <FolderIcon
        className={cn(
          "h-4 w-4 shrink-0 transition-transform",
          isDragOver && isDragging && "scale-110"
        )}
        style={{ color: folder.color }}
      />

      {/* Folder name */}
      {!collapsed && (
        <span className="text-sm font-medium truncate flex-1">
          {folder.name}
        </span>
      )}

      {/* More menu button - only on hover */}
      {!collapsed && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <button className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-muted transition-opacity">
              <MoreHorizontal className="h-3.5 w-3.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40">
            <DropdownMenuItem onClick={() => { setNewName(folder.name); setRenameDialogOpen(true); }}>
              <Pencil className="h-4 w-4 mr-2" />
              Renommer
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => { setNewColor(folder.color); setColorDialogOpen(true); }}>
              <Palette className="h-4 w-4 mr-2" />
              Couleur
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={handleDeleteClick}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Supprimer
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Active indicator */}
      {isActive && (
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 rounded-full bg-primary" />
      )}
    </button>
  );

  return (
    <div>
      {/* Context menu wrapper for right-click */}
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              {folderButton}
            </TooltipTrigger>
            {collapsed && (
              <TooltipContent side="right" sideOffset={12}>
                {folder.name}
              </TooltipContent>
            )}
          </Tooltip>
        </ContextMenuTrigger>
        <ContextMenuContent className="w-40">
          <ContextMenuItem onClick={() => { setNewName(folder.name); setRenameDialogOpen(true); }}>
            <Pencil className="h-4 w-4 mr-2" />
            Renommer
          </ContextMenuItem>
          <ContextMenuItem onClick={() => { setNewColor(folder.color); setColorDialogOpen(true); }}>
            <Palette className="h-4 w-4 mr-2" />
            Couleur
          </ContextMenuItem>
          <ContextMenuSeparator />
          <ContextMenuItem 
            onClick={handleDeleteClick}
            className="text-destructive focus:text-destructive"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Supprimer
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>

      {/* Children - recursive */}
      {hasChildren && isExpanded && !collapsed && (
        <div className="mt-0.5">
          {folder.children.map((child) => (
            <FolderItem
              key={child.id}
              folder={child}
              level={level + 1}
              collapsed={collapsed}
              onRename={onRename}
              onDelete={onDelete}
              onColorChange={onColorChange}
              onRefetch={onRefetch}
            />
          ))}
        </div>
      )}

      {/* Rename Dialog */}
      <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Renommer le dossier</DialogTitle>
            <DialogDescription>
              Entrez un nouveau nom pour "{folder.name}"
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="new-name" className="sr-only">Nouveau nom</Label>
            <Input
              id="new-name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Nouveau nom"
              autoFocus
              onKeyDown={(e) => e.key === "Enter" && handleRename()}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleRename} disabled={!newName.trim() || isSubmitting}>
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Renommer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Color Dialog */}
      <Dialog open={colorDialogOpen} onOpenChange={setColorDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Changer la couleur</DialogTitle>
            <DialogDescription>
              Choisissez une couleur pour "{folder.name}"
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="flex flex-wrap gap-3 justify-center">
              {FOLDER_COLORS.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => handleColorChange(c.value)}
                  disabled={isSubmitting}
                  className={cn(
                    "w-10 h-10 rounded-full transition-all duration-200 flex items-center justify-center",
                    newColor === c.value
                      ? "ring-2 ring-offset-2 ring-primary scale-110"
                      : "hover:scale-105"
                  )}
                  style={{ backgroundColor: c.value }}
                  title={c.label}
                >
                  {newColor === c.value && (
                    <svg
                      className="w-5 h-5 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer "{folder.name}" ?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                {folderStats && (folderStats.documents > 0 || folderStats.subfolders > 0) ? (
                  <>
                    <p>Ce dossier contient :</p>
                    <ul className="list-disc list-inside text-sm">
                      {folderStats.documents > 0 && (
                        <li>{folderStats.documents} document{folderStats.documents > 1 ? "s" : ""}</li>
                      )}
                      {folderStats.subfolders > 0 && (
                        <li>{folderStats.subfolders} sous-dossier{folderStats.subfolders > 1 ? "s" : ""}</li>
                      )}
                    </ul>
                    <p className="text-muted-foreground">
                      Les documents et sous-dossiers seront déplacés à la racine.
                    </p>
                  </>
                ) : (
                  <p>Ce dossier est vide et sera supprimé définitivement.</p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isSubmitting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
